import { useEffect, useState, useCallback } from "react";
import * as adminTradingBotService from "../../services/adminTradingBotService";
import type { TradingBotConfig } from "../../types/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Checkbox } from "../ui/checkbox";
import { Skeleton } from "../ui/skeleton";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useToast } from "../ui/use-toast";
import { useTranslation } from "react-i18next";

export interface ClientForBots {
  id: number;
  firstName: string;
  lastName: string;
}

type Props = {
  client: ClientForBots | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const ClientBotsModal = ({ client, open, onOpenChange }: Props) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [configs, setConfigs] = useState<TradingBotConfig[]>([]);
  const [maxTradingBots, setMaxTradingBots] = useState<number>(1);
  const [maxTradingBotsInput, setMaxTradingBotsInput] = useState<string>("1");
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [savingMax, setSavingMax] = useState(false);

  useEffect(() => {
    if (!open || !client) {
      setConfigs([]);
      setMaxTradingBots(1);
      setMaxTradingBotsInput("1");
      return;
    }
    let isMounted = true;
    setLoading(true);
    adminTradingBotService
      .listClientConfigs(client.id)
      .then((result) => {
        if (isMounted) {
          setConfigs(result.configs);
          setMaxTradingBots(result.maxTradingBots);
          setMaxTradingBotsInput(String(result.maxTradingBots));
        }
      })
      .catch(() => {
        if (isMounted) setConfigs([]);
        toast({
          title: t("errors.generic", "Erreur"),
          description: t("tradingBot.admin.loadError", "Impossible de charger les robots du client."),
          variant: "destructive",
        });
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [open, client, toast, t]);

  const handleToggle = useCallback(
    async (config: TradingBotConfig, enabled: boolean) => {
      if (!client) return;
      setTogglingId(config.id);
      try {
        await adminTradingBotService.updateClientConfigEnabled(client.id, config.id, enabled);
        setConfigs((prev) => prev.map((c) => (c.id === config.id ? { ...c, enabled } : c)));
        toast({
          title: t("common.saved", "Enregistré"),
          description: enabled
            ? t("tradingBot.admin.activated", "Robot activé.")
            : t("tradingBot.admin.deactivated", "Robot désactivé."),
        });
      } catch (err: any) {
        toast({
          title: t("errors.generic", "Erreur"),
          description: err?.message ?? "Impossible de modifier l'état du robot",
          variant: "destructive",
        });
      } finally {
        setTogglingId(null);
      }
    },
    [client, toast, t]
  );

  const handleSaveMaxTradingBots = useCallback(async () => {
    if (!client) return;
    const num = parseInt(maxTradingBotsInput, 10);
    if (Number.isNaN(num) || num < 0 || num > 20) {
      toast({
        title: t("errors.generic", "Erreur"),
        description: t("tradingBot.admin.maxBotsInvalid", "Nombre entre 0 et 20."),
        variant: "destructive",
      });
      return;
    }
    setSavingMax(true);
    try {
      await adminTradingBotService.updateClientMaxTradingBots(client.id, num);
      setMaxTradingBots(num);
      toast({
        title: t("common.saved", "Enregistré"),
        description: t("tradingBot.admin.maxBotsSaved", "Limite de robots mise à jour."),
      });
    } catch (err: any) {
      toast({
        title: t("errors.generic", "Erreur"),
        description: err?.message ?? "Impossible de sauvegarder",
        variant: "destructive",
      });
    } finally {
      setSavingMax(false);
    }
  }, [client, maxTradingBotsInput, toast, t]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-lg border-white/10 bg-slate-950 text-white">
        <DialogHeader>
          <DialogTitle>
            {t("tradingBot.admin.clientBotsTitle", "Robots de trading")}
            {client && ` — ${client.firstName} ${client.lastName}`}
          </DialogTitle>
          <DialogDescription className="text-white/60">
            {t(
              "tradingBot.admin.clientBotsDescription",
              "Activer ou désactiver les robots configurés par le client. Définir le nombre max de robots autorisés."
            )}
          </DialogDescription>
        </DialogHeader>
        {!loading && client && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-4 py-3">
            <label className="text-sm text-white/80">
              {t("tradingBot.admin.maxBotsPerClient", "Nombre max de robots pour ce client :")}
            </label>
            <Input
              type="number"
              min={0}
              max={20}
              value={maxTradingBotsInput}
              onChange={(e) => setMaxTradingBotsInput(e.target.value)}
              className="h-9 w-20 bg-white/10 text-white"
            />
            <Button
              size="sm"
              variant="secondary"
              onClick={handleSaveMaxTradingBots}
              disabled={savingMax || String(maxTradingBots) === maxTradingBotsInput}
            >
              {savingMax ? t("common.saving", "Enregistrement…") : t("common.save", "Enregistrer")}
            </Button>
            <span className="text-xs text-white/60">
              {configs.length} / {maxTradingBots}
            </span>
          </div>
        )}
        {loading ? (
          <Skeleton className="h-24 w-full" />
        ) : configs.length === 0 ? (
          <p className="text-sm text-white/60">
            {t("tradingBot.admin.noBots", "Aucun robot configuré.")}
          </p>
        ) : (
          <div className="space-y-3">
            {configs.map((config) => (
              <div
                key={config.id}
                className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 px-4 py-3">
                <div>
                  <span className="font-medium">{config.symbol}</span>
                  <span className="ml-2 text-sm text-white/60">{config.strategyType}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={config.enabled}
                    onCheckedChange={(checked) => handleToggle(config, !!checked)}
                    disabled={togglingId === config.id}
                  />
                  <span className="text-xs text-white/60">
                    {config.enabled
                      ? t("tradingBot.active", "Actif")
                      : t("tradingBot.disabled", "Inactif")}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
